import {
  createContext,
  useContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from "react";

const PlayerCtx = createContext(null);
export const usePlayer = () => useContext(PlayerCtx);

export function PlayerProvider({ children }) {
  const audioRef = useRef(null);
  const pendingSeek = useRef(null);
  const [current, setCurrent] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRateState] = useState(1);

  useEffect(() => {
    const a = new Audio();
    a.preload = "metadata";
    audioRef.current = a;

    const onTime = () => setTime(a.currentTime);
    const onMeta = () => {
      setDuration(a.duration || 0);
      if (pendingSeek.current != null) {
        a.currentTime = pendingSeek.current;
        pendingSeek.current = null;
      }
    };
    const onEnded = () => setIsPlaying(false);
    const onPlay = () => setIsPlaying(true);
    const onPause = () => setIsPlaying(false);

    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("durationchange", onMeta);
    a.addEventListener("ended", onEnded);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);

    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("durationchange", onMeta);
      a.removeEventListener("ended", onEnded);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
    };
  }, []);

  const playEpisode = useCallback(
    (ep, startTime = 0) => {
      const a = audioRef.current;
      if (!a || !ep) return;
      if (!current || current.id !== ep.id) {
        a.src = ep.audioUrl;
        setCurrent(ep);
        setTime(0);
        setDuration(ep.durationSeconds || 0);
        pendingSeek.current = startTime || null;
      } else if (startTime) {
        a.currentTime = startTime;
      }
      a.playbackRate = rate;
      a.play().catch(() => {});
    },
    [current, rate],
  );

  const toggle = useCallback(() => {
    const a = audioRef.current;
    if (!a || !current) return;
    if (a.paused) a.play().catch(() => {});
    else a.pause();
  }, [current]);

  const seek = useCallback((t) => {
    const a = audioRef.current;
    if (a) {
      a.currentTime = t;
      setTime(t);
    }
  }, []);

  const skip = useCallback((delta) => {
    const a = audioRef.current;
    if (a) {
      const d = a.duration || 0;
      a.currentTime = Math.max(0, Math.min(d, a.currentTime + delta));
    }
  }, []);

  const setRate = useCallback((r) => {
    setRateState(r);
    if (audioRef.current) audioRef.current.playbackRate = r;
  }, []);

  const value = {
    current,
    isPlaying,
    time,
    duration,
    rate,
    playEpisode,
    toggle,
    seek,
    skip,
    setRate,
    isActive: (ep) => current?.id === ep?.id,
  };

  return <PlayerCtx.Provider value={value}>{children}</PlayerCtx.Provider>;
}
